import type { HttpServiceRequestConfig, HttpServiceResponse } from '@/services/HttpService/types';
import { useCallback, useEffect, useState } from 'react';
import { HttpMethods } from '@/enums/HttpMethods';
import CacheService from '@/services/CacheService/CacheService';
import { CACHE_SERVICE_DEFAULT_TIME_OF_LIFE } from '@/services/CacheService/constants/cacheDefaultTimeOfLife';
import HttpService from '@/services/HttpService/HttpService';

type Params = {
  method?: HttpMethods;
  body?: Record<string, any>;
  queryParams?: Record<string, any>;
  config?: HttpServiceRequestConfig;
};

export type UseFetchReturn<T> = {
  data: T;
  error: any;
  isLoading: boolean;
  refetch: (resetCache?: boolean) => Promise<T | undefined>;
  resetData: () => void;
  wasTriggered: boolean;
};

export const useFetch = <T>(
  url: string,
  params?: Params | null,
  skip?: boolean,
  cache?: { timeMs?: number; resetCache?: boolean; dependencies?: any[] },
  retryOnFail?: boolean,
  maxRetries = 1,
) => {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [wasTriggered, setWasTriggered] = useState(false);

  const fetchData = useCallback(
    async (resetCache: boolean = true) => {
      setIsLoading(true);
      setError(null);

      let retryAttempts = 0;
      const canMakeResponse = retryAttempts <= maxRetries;

      while (canMakeResponse) {
        try {
          const method = params?.method ?? HttpMethods.GET;

          const requestBody = params?.body ?? {};

          const queryString = params?.queryParams ? `?${new URLSearchParams(params.queryParams).toString()}` : '';
          const fullUrl = `${url}${queryString}`;

          const cacheVersion = JSON.stringify({ method, body: requestBody });
          const cachedData = CacheService.get<T>(fullUrl, Boolean(cache?.resetCache || resetCache), cacheVersion);

          let data: T;

          if (cachedData) {
            data = cachedData;
          } else {
            const response: HttpServiceResponse<T> = await HttpService[method](fullUrl, requestBody, params?.config);
            data = response.data;
            CacheService.set<T>(
              fullUrl,
              response.data,
              cache?.timeMs || CACHE_SERVICE_DEFAULT_TIME_OF_LIFE,
              cacheVersion,
            );
          }

          setData(data);
          setIsLoading(false);

          return data;
        } catch (error) {
          retryAttempts++;

          if (retryOnFail && canMakeResponse) {
            await new Promise((resolve) => setTimeout(resolve, 3000));
          } else {
            setError(error);
            setIsLoading(false);
            return;
          }
        } finally {
          setWasTriggered(true);
        }
      }
    },
    [url, params, retryOnFail, maxRetries],
  );

  const resetData = useCallback(() => setData(null), []);

  useEffect(() => {
    const queryString = params?.queryParams ? `?${new URLSearchParams(params.queryParams).toString()}` : '';
    const fullUrl = `${url}${queryString}`;

    CacheService.remove(fullUrl);
  }, [url, params?.queryParams, ...(cache?.dependencies || [])]);

  useEffect(() => {
    if (!skip) fetchData(false);
  }, [fetchData, skip]);

  return { data, error, isLoading, refetch: fetchData, resetData, wasTriggered };
};
